import { useState, useEffect } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  MessageSquare, 
  User, 
  AlertCircle, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Flag,
  Shield,
  Mail,
  Calendar,
  FileText,
  Eye,
  Gavel
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { QuickStatusActions } from "./QuickStatusActions";
import { JobPhotosSection } from "./JobPhotosSection";

interface Message {
  id: string;
  content: string;
  sender_id: string;
  recipient_id: string;
  created_at: string;
  is_blocked: boolean;
  blocked_reason?: string;
  sender?: {
    full_name: string;
    email?: string;
    user_type: string;
  };
  recipient?: {
    full_name: string;
    email?: string;
    user_type: string;
  };
}

interface UserProfile {
  id: string;
  full_name: string;
  email: string;
  user_type: string;
  company_name?: string;
  trade_specialties?: string[];
  location?: string;
  phone: string;
  is_suspended: boolean;
  suspension_reason?: string;
  suspended_until?: string;
  insurance_approved: boolean;
  email_verified: boolean;
  phone_verified: boolean;
  average_rating?: number;
  total_reviews?: number;
  created_at: string;
}

interface ReportDetailDialogProps {
  report: any;
  isOpen: boolean;
  onClose: () => void;
  onStatusUpdate: (reportId: string, status: string, notes?: string) => void;
}

export const ReportDetailDialog = ({ report, isOpen, onClose, onStatusUpdate }: ReportDetailDialogProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [participantNames, setParticipantNames] = useState<Record<string, string>>({});
  const { toast } = useToast();

  useEffect(() => {
    if (report && isOpen) {
      setAdminNotes(report.admin_notes || "");
      setNewStatus(report.status || "pending");
      
      if ((report.report_type === 'chat' || report.report_type === 'tradesman_account' || report.report_type === 'job_issue') && report.job?.id) {
        fetchChatMessages();
        fetchParticipantNames();
      }
      
      if (report.reported_user?.id) {
        fetchUserProfile();
      }
    }
  }, [report, isOpen]);

  const fetchChatMessages = async () => {
    console.log('Fetching messages for report:', report);
    console.log('Report job:', report.job);
    
    // For chat reports and account reports with job context, job_id is accessible from report table
    const jobId = report.job?.id;
    if (!jobId) {
      console.error('No job ID found in report:', report);
      return;
    }
    
    setIsLoading(true);
    try {
      console.log('Fetching messages for job ID:', jobId, 'Report type:', report.report_type);
      
      // First get messages
      const { data: messagesData, error: messagesError } = await supabase
        .from("messages")
        .select(`
          id,
          content,
          created_at,
          sender_id,
          recipient_id,
          is_blocked,
          blocked_reason
        `)
        .eq("job_id", jobId)
        .order("created_at", { ascending: true });

      console.log('Messages query result:', { messagesData, messagesError });

      if (messagesError) throw messagesError;

      if (!messagesData || messagesData.length === 0) {
        setMessages([]);
        setIsLoading(false);
        return;
      }

      // Get unique user IDs from messages
      const userIds = Array.from(new Set([
        ...messagesData.map(m => m.sender_id),
        ...messagesData.map(m => m.recipient_id)
      ].filter(Boolean)));

      // Fetch profiles for all users involved in messages
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name, email, user_type")
        .in("id", userIds);

      if (profilesError) throw profilesError;

      // Create a map of profiles by id
      const profilesMap = new Map();
      (profilesData || []).forEach(profile => {
        profilesMap.set(profile.id, profile);
      });

      // Enrich messages with profile data
      const enrichedMessages = messagesData.map(message => ({
        ...message,
        sender: profilesMap.get(message.sender_id) || null,
        recipient: profilesMap.get(message.recipient_id) || null
      }));

      setMessages(enrichedMessages);
    } catch (error: any) {
      console.error("Error fetching messages:", error);
      toast({
        title: "Error loading chat",
        description: "Could not load chat messages for this report.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchParticipantNames = async () => {
    if (!report?.job?.id) return;

    try {
      const { data, error } = await supabase.functions.invoke('get-job-participant-names', {
        body: { jobId: report.job.id }
      });

      if (error) throw error;
      
      setParticipantNames(data.names || {});
    } catch (error) {
      console.error("Error fetching participant names:", error);
      // Don't show error toast as this is not critical for admins
    }
  };

  const fetchUserProfile = async () => {
    if (!report.reported_user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", report.reported_user.id)
        .single();

      if (error) throw error;
      setUserProfile(data);
    } catch (error: any) {
      console.error("Error fetching user profile:", error);
      toast({
        title: "Error loading profile",
        description: "Could not load user profile details.",
        variant: "destructive",
      });
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) return;
    
    setIsUpdating(true);
    try {
      await onStatusUpdate(report.id, newStatus, adminNotes.trim() || undefined);
      onClose();
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'reviewed': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'resolved': return 'bg-green-100 text-green-800 border-green-300';
      case 'dismissed': return 'bg-gray-100 text-gray-800 border-gray-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (!report) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-red-500" />
            Report Details - {report.report_type === 'chat' ? 'Chat Report' : report.report_type === 'job_issue' ? 'Job Issue Report' : 'Account Report'}
          </DialogTitle>
          <DialogDescription>
            Reported on {format(new Date(report.created_at), "PPP")} by {report.reporter?.full_name}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          <Tabs defaultValue="overview" className="h-full flex flex-col">
            <TabsList className="grid w-full grid-cols-3 flex-shrink-0">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              {((report.report_type === 'chat') || (report.report_type === 'tradesman_account' && report.job?.id) || (report.report_type === 'job_issue')) && (
                <TabsTrigger value="chat">{report.report_type === 'job_issue' ? 'Job Details & Messages' : 'Chat History'}</TabsTrigger>
              )}
              <TabsTrigger value="account">Account Details</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="flex-1 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="space-y-6 p-4">
                  {/* Report Summary */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertCircle className="h-5 w-5" />
                        Report Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium">Report Type</Label>
                          <p className="text-sm text-muted-foreground capitalize">{report.report_type}</p>
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Status</Label>
                          <Badge className={`${getStatusColor(report.status)} capitalize`}>
                            {report.status}
                          </Badge>
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Reason</Label>
                          <p className="text-sm text-muted-foreground">{report.reason}</p>
                        </div>
                        <div>
                          <Label className="text-sm font-medium">Reported User</Label>
                          <p className="text-sm text-muted-foreground">
                            {report.reported_user?.full_name} ({report.reported_user?.user_type})
                          </p>
                        </div>
                      </div>
                      
                      {report.description && (
                        <div>
                          <Label className="text-sm font-medium">Description</Label>
                          <p className="text-sm text-muted-foreground mt-1">{report.description}</p>
                        </div>
                      )}

                      {report.job && (
                        <div>
                          <Label className="text-sm font-medium">Related Job</Label>
                          <p className="text-sm text-muted-foreground">{report.job.title}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Job Photos Section */}
                  {report.job && (
                    <JobPhotosSection 
                      jobId={report.job.id} 
                      evidenceUrls={report.evidence_urls}
                    />
                  )}

                  {/* Quick Status Actions */}
                  <QuickStatusActions
                    report={report}
                    onStatusUpdate={onStatusUpdate}
                    isUpdating={isUpdating}
                  />

                  {/* Admin Actions */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Gavel className="h-5 w-5" />
                        Admin Actions
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label htmlFor="status">Update Status</Label>
                        <Select value={newStatus} onValueChange={setNewStatus}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="reviewed">Under Review</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                            <SelectItem value="dismissed">Dismissed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="notes">Admin Notes</Label>
                        <Textarea
                          id="notes"
                          value={adminNotes}
                          onChange={(e) => setAdminNotes(e.target.value)}
                          placeholder="Add notes about your investigation or actions taken..."
                          rows={4}
                        />
                      </div>

                      <Button 
                        onClick={handleStatusUpdate} 
                        disabled={isUpdating}
                        className="w-full"
                      >
                        {isUpdating ? "Updating..." : "Update Report"}
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </ScrollArea>
            </TabsContent>

            {((report.report_type === 'chat') || (report.report_type === 'tradesman_account' && report.job?.id) || (report.report_type === 'job_issue')) && (
              <TabsContent value="chat" className="flex-1 overflow-hidden">
                <Card className="h-full flex flex-col">
                  <CardHeader className="flex-shrink-0">
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5" />
                      {report.report_type === 'job_issue' ? 'Job Issue Details & Messages' : 
                       report.report_type === 'chat' ? 'Chat Messages' : 'Related Chat History'}
                    </CardTitle>
                    <CardDescription>
                      {report.report_type === 'job_issue' 
                        ? `Issue description and conversation history for job: ${report.job?.title}`
                        : report.report_type === 'chat' 
                        ? `Full conversation history for job: ${report.job?.title}`
                        : `Chat messages related to the reported account for job: ${report.job?.title}`
                      }
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-hidden p-0">
                    <ScrollArea className="h-full p-4">
                      {report.report_type === 'job_issue' && report.description && (
                        <Card className="mb-4 bg-red-50 border-red-200">
                          <CardHeader>
                            <CardTitle className="text-sm flex items-center gap-2">
                              <AlertCircle className="h-4 w-4 text-red-600" />
                              Issue Description
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="text-sm text-red-800">{report.description}</p>
                          </CardContent>
                        </Card>
                      )}
                      
                      {isLoading ? (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
                          <p className="text-sm text-muted-foreground">Loading messages...</p>
                        </div>
                      ) : messages.length === 0 ? (
                        <div className="text-center py-8">
                          <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                          <p className="text-sm text-muted-foreground">No messages found</p>
                        </div>
                      ) : (
                         <div className="space-y-3">
                           {messages.map((message, index) => {
                             const senderName = participantNames[message.sender_id] || 'User';
                             const senderType = message.sender?.user_type || 'user';
                             
                             return (
                              <div
                                key={message.id}
                                className={`${
                                  message.is_blocked 
                                    ? 'bg-red-50 border-red-200 border' 
                                    : senderType === 'customer' 
                                      ? 'bg-blue-50 border-blue-200 border' 
                                      : 'bg-green-50 border-green-200 border'
                                } rounded-lg p-3`}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <Badge 
                                      variant={senderType === 'customer' ? 'default' : 'secondary'} 
                                      className="text-xs"
                                    >
                                      {senderType === 'customer' ? 'Customer' : 'Tradesman'}
                                    </Badge>
                                    <span className="text-sm font-medium">{senderName}</span>
                                  </div>
                                  <span className="text-xs text-muted-foreground">
                                    {format(new Date(message.created_at), "MMM d, HH:mm")}
                                  </span>
                                </div>
                                
                                {message.is_blocked ? (
                                  <div className="flex items-center gap-2 text-red-600">
                                    <AlertCircle className="h-4 w-4" />
                                    <span className="text-sm font-medium">
                                      Blocked: {message.blocked_reason}
                                    </span>
                                  </div>
                                ) : (
                                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            <TabsContent value="account" className="flex-1 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="space-y-6 p-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="h-5 w-5" />
                        User Profile Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {userProfile ? (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm font-medium">Full Name</Label>
                              <p className="text-sm text-muted-foreground">{userProfile.full_name}</p>
                            </div>
                            <div>
                              <Label className="text-sm font-medium">Email</Label>
                              <div className="flex items-center gap-2">
                                <Mail className="h-4 w-4" />
                                <p className="text-sm text-muted-foreground">{userProfile.email}</p>
                                {userProfile.email_verified && (
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                )}
                              </div>
                            </div>
                            <div>
                              <Label className="text-sm font-medium">User Type</Label>
                              <Badge variant="outline" className="capitalize">
                                {userProfile.user_type}
                              </Badge>
                            </div>
                            <div>
                              <Label className="text-sm font-medium">Phone</Label>
                              <div className="flex items-center gap-2">
                                <p className="text-sm text-muted-foreground">{userProfile.phone}</p>
                                {userProfile.phone_verified && (
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                )}
                              </div>
                            </div>
                            <div>
                              <Label className="text-sm font-medium">Member Since</Label>
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                <p className="text-sm text-muted-foreground">
                                  {format(new Date(userProfile.created_at), "PPP")}
                                </p>
                              </div>
                            </div>
                            <div>
                              <Label className="text-sm font-medium">Account Status</Label>
                              <div className="flex items-center gap-2">
                                {userProfile.is_suspended ? (
                                  <>
                                    <XCircle className="h-4 w-4 text-red-600" />
                                    <Badge variant="destructive">Suspended</Badge>
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                    <Badge variant="outline" className="text-green-600">Active</Badge>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Tradesman specific details */}
                          {userProfile.user_type === 'tradesman' && (
                            <div className="mt-6 p-4 border rounded-lg">
                              <h4 className="font-medium mb-3 flex items-center gap-2">
                                <Shield className="h-4 w-4" />
                                Tradesman Details
                              </h4>
                              <div className="grid grid-cols-2 gap-4">
                                {userProfile.company_name && (
                                  <div>
                                    <Label className="text-sm font-medium">Company Name</Label>
                                    <p className="text-sm text-muted-foreground">{userProfile.company_name}</p>
                                  </div>
                                )}
                                {userProfile.location && (
                                  <div>
                                    <Label className="text-sm font-medium">Location</Label>
                                    <p className="text-sm text-muted-foreground">{userProfile.location}</p>
                                  </div>
                                )}
                                <div>
                                  <Label className="text-sm font-medium">Insurance Status</Label>
                                  <div className="flex items-center gap-2">
                                    {userProfile.insurance_approved ? (
                                      <>
                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                        <span className="text-sm text-green-600">Approved</span>
                                      </>
                                    ) : (
                                      <>
                                        <XCircle className="h-4 w-4 text-red-600" />
                                        <span className="text-sm text-red-600">Not Approved</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                                {userProfile.average_rating && (
                                  <div>
                                    <Label className="text-sm font-medium">Rating</Label>
                                    <p className="text-sm text-muted-foreground">
                                      {userProfile.average_rating}/5 ({userProfile.total_reviews} reviews)
                                    </p>
                                  </div>
                                )}
                              </div>
                              {userProfile.trade_specialties && userProfile.trade_specialties.length > 0 && (
                                <div className="mt-3">
                                  <Label className="text-sm font-medium">Specialties</Label>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {userProfile.trade_specialties.map((specialty, index) => (
                                      <Badge key={index} variant="secondary" className="text-xs">
                                        {specialty}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Suspension details */}
                          {userProfile.is_suspended && (
                            <div className="mt-6 p-4 border border-red-200 bg-red-50 rounded-lg">
                              <h4 className="font-medium mb-3 flex items-center gap-2 text-red-700">
                                <XCircle className="h-4 w-4" />
                                Suspension Details
                              </h4>
                              {userProfile.suspension_reason && (
                                <div className="mb-2">
                                  <Label className="text-sm font-medium">Reason</Label>
                                  <p className="text-sm text-muted-foreground">{userProfile.suspension_reason}</p>
                                </div>
                              )}
                              {userProfile.suspended_until && (
                                <div>
                                  <Label className="text-sm font-medium">Until</Label>
                                  <p className="text-sm text-muted-foreground">
                                    {format(new Date(userProfile.suspended_until), "PPP")}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                          <p className="text-sm text-muted-foreground">Loading user profile...</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};